<script setup>

/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { defineProps, defineExpose, ref, computed } from 'vue';

/*==================================================================*/
/*================== IMPORTS UTILS =================================*/
/*==================================================================*/

import UtilArray from '@/utils/UtilArray.mjs';

/*==================================================================*/
/*================== IMPORTS DE COMPONENTES ========================*/
/*==================================================================*/

import ModalFullscreen from '@/components/v2/modais/ModalFullscreen/ModalFullscreen.vue';
import ConteudoModal from './ConteudoModal.vue';
import UtilURL from '@/utils/UtilURL';

/*==================================================================*/
/*================== PROPS =========================================*/
/*==================================================================*/

const props = defineProps({
    onClose: {
        type: Function,
        default: null
    }
});

/*==================================================================*/
/*================== MODAL FULLSCREEN REF ==========================*/
/*==================================================================*/

const modalFullscreenRef = ref();

/*==================================================================*/
/*================== DADO ==========================================*/
/*==================================================================*/

const dado = ref({});

/*==================================================================*/
/*================== PAGAMENTO =====================================*/
/*==================================================================*/

const pagamento = computed(() => {

    return dado?.value?.parcelas?.[0] || {};

});

/*==================================================================*/
/*================== APROVACAO TIPO ================================*/
/*==================================================================*/

const aprovacaoTipo = computed(() => {

    let status = dado?.value?.status || '';
    let tipo = 'Nenhum';

    switch(status){
        case 'Aguardando Requisitante':
            tipo = 'Requisitante';
        break;
        case 'Aguardando Coordenador':
            tipo = 'Coordenador';
        break;
        case 'Aguardando Gestor':
            tipo = 'Gestor';
        break;
        case 'Aguardando Financeiro':
            tipo = 'Financeiro';
        break;
        default:
            tipo = 'Nenhum';
    }

    return tipo;

});

/*==================================================================*/
/*=================== APROVACOES REQUISITANTES =====================*/
/*==================================================================*/

const aprovacoesRequisitantes = computed(() => {

    return dado?.value?.aprovadores?.filter((aprovador = {}) => {

        return aprovador?.aprovadorTipo === 'Requisitante';

    });

});

/*==================================================================*/
/*=================== APROVACOES COORDENADORES =====================*/
/*==================================================================*/

const aprovacoesCoordenadores = computed(() => {

    return dado?.value?.aprovadores?.filter((aprovador = {}) => {

        return aprovador?.aprovadorTipo === 'Coordenador';

    });

});

/*==================================================================*/
/*=================== APROVACOES GESTORES ==========================*/
/*==================================================================*/

const aprovacoesGestores = computed(() => {

    return dado?.value?.aprovadores?.filter((aprovador = {}) => {

        return aprovador?.aprovadorTipo === 'Gestor';

    });

});

/*==================================================================*/
/*=================== APROVACOES FINANCEIROS =======================*/
/*==================================================================*/

const aprovacoesFinanceiros = computed(() => {

    return dado?.value?.aprovadores?.filter((aprovador = {}) => {

        return aprovador?.aprovadorTipo === 'Financeiro';

    });

});

/*==================================================================*/
/*================== APROVADORES PARA EXIBICAO =====================*/
/*==================================================================*/

const aprovadoresParaExibicao = computed(() => {

    let tipoAtual = aprovacaoTipo?.value ?? 'Nenhum';

    if(tipoAtual === 'Requisitante'){

        return aprovacoesRequisitantes?.value;

    }

    if(tipoAtual === 'Coordenador'){

        return UtilArray.concat(
            aprovacoesRequisitantes?.value,
            aprovacoesCoordenadores?.value
        );

    }

    if(tipoAtual === 'Gestor'){

        return UtilArray.concat(
            aprovacoesRequisitantes?.value,
            aprovacoesCoordenadores?.value,
            aprovacoesGestores?.value
        );

    }

    if(tipoAtual === 'Financeiro'){

        return UtilArray.concat(
            aprovacoesRequisitantes?.value,
            aprovacoesCoordenadores?.value,
            aprovacoesGestores?.value,
            aprovacoesFinanceiros?.value
        );

    }

    return dado?.value?.aprovadores ?? [];

});

/*==================================================================*/
/*================== PREPARA DESPESAS ==============================*/
/*==================================================================*/

const preparaDespesas = async (despesas = []) => {

    const formataDespesa = (despesa = {}) => {
        return `${despesa?.codigo ?? ''} - ${despesa?.descricao ?? ''} - (Veicular: ${despesa?.veicular ?? ''})`;
    };
    
    let despesasAUX = [];

    for(const despesa of despesas ?? []){

        let despesaAUX = {
            id: despesa.id ?? '',
            numero: despesa.numero ?? '',
            despesa: despesa.despesa ?? {},
            despesaFormatada: formataDespesa(despesa.despesa),
            dataDaDespesa: despesa.dataDaDespesa ?? '',
            dataCriacao: despesa.dataCriacao ?? '',
            valor: despesa.valor ?? 0,
            veicular: despesa.veicular ?? false,
            veiculoTipo: despesa.veiculoTipo ?? '',
            veiculoPlaca: despesa.veiculoPlaca ?? '',
            veiculoKMInicial: despesa.veiculoKMInicial ?? 0,
            veiculoKMFinal: despesa.veiculoKMFinal ?? 0,
            observacao: despesa.observacao ?? '',
            anexos: await UtilURL.manyToFile(despesa?.anexos || []),
        }

        despesasAUX.push(despesaAUX);

    }

    return despesasAUX;

};

/*==================================================================*/
/*================== PREPARA PARCELAS ==============================*/
/*==================================================================*/

const preparaParcelas = (parcelas = []) => {

    return parcelas.map((parcela = {}) => ({

        id: parcela.id ?? '',
        numero: parcela.numero ?? '',
        descricao: parcela.descricao ?? '',
        dataCriacao: parcela.dataCriacao ?? '',
        dataDoPagamento: parcela.dataDoPagamento ?? '',
        valor: parcela.valor ?? 0,

    }));

};

/*==================================================================*/
/*================== PREPARA APROVADORES ===========================*/
/*==================================================================*/

const preparaAprovadores = (aprovadores = []) => {

    return aprovadores.map((aprovador = {}) => ({

        id: aprovador.id ?? '',
        codigo: aprovador.codigo ?? '',
        status: aprovador.status ?? 'Valido',
        aprovadorTipo: aprovador.aprovadorTipo ?? 'Nenhum',
        aprovador: aprovador.aprovador ?? {},
        decisao: aprovador.decisao ?? 'Nula',
        justificativa: aprovador.justificativa ?? '',
        dataDecisao: aprovador.dataDecisao ?? '',

    }));

};

/*==================================================================*/
/*================== PREPARA DADOS =================================*/
/*==================================================================*/

const preparaDados = async (dadoParaPreparar = {}) => {

    return {
        id: dadoParaPreparar.id ?? '',
        numero: dadoParaPreparar.numero ?? '',
        status: dadoParaPreparar.status ?? '',
        emitente: dadoParaPreparar.emitente ?? {},
        requisitante: dadoParaPreparar.requisitante ?? {},
        tipo: dadoParaPreparar.tipo ?? '',
        notaFiscal: dadoParaPreparar.notaFiscal ?? '',
        tipoCredor: dadoParaPreparar.tipoCredor ?? '',
        credorColaborador: dadoParaPreparar.credorColaborador ?? {},
        credorFornecedor: dadoParaPreparar.credorFornecedor ?? {},
        formaDePagamento: dadoParaPreparar.formaDePagamento ?? '',
        recorrencia: dadoParaPreparar.recorrencia ?? false,
        recorrenciaQuantidade: dadoParaPreparar.recorrenciaQuantidade ?? 0,
        dataCriacao: dadoParaPreparar.dataCriacao ?? '',
        dataPrevisaoPagamento: dadoParaPreparar.dataPrevisaoPagamento ?? '',
        dataAprovacaoPagamento: dadoParaPreparar.dataAprovacaoPagamento ?? '',
        valorTotal: dadoParaPreparar.valorTotal ?? 0,
        justificativa: dadoParaPreparar.justificativa ?? '',
        job: dadoParaPreparar.job ?? {},
        ordemDeServico: dadoParaPreparar.ordemDeServico ?? "",
        owners: dadoParaPreparar.owners ?? [],
        despesas: await preparaDespesas(dadoParaPreparar.despesas ?? []),
        parcelas: preparaParcelas(dadoParaPreparar.parcelas ?? []),
        aprovadores: preparaAprovadores(dadoParaPreparar.aprovadores ?? []),
    };

};

/*==================================================================*/
/*================== ABRIR MODAL ===================================*/
/*==================================================================*/

const abrirModal = async (dadoParaVisualizar = {}) => {

    dado.value = await preparaDados(dadoParaVisualizar);

    await modalFullscreenRef.value?.abrirModal();

};

/*==================================================================*/
/*================== FECHAR MODAL ==================================*/
/*==================================================================*/

const fecharModal = async () => {

    await modalFullscreenRef.value?.fecharModal();

};

/*==================================================================*/
/*================== AO FECHAR MODAL ===============================*/
/*==================================================================*/

const aoFecharModal = async () => {

    await props.onClose?.();

};

/*==================================================================*/
/*================== EXPOSE ========================================*/
/*==================================================================*/

defineExpose({ abrirModal, fecharModal });

</script>

<template>

    <ModalFullscreen
        ref="modalFullscreenRef"
        :dado="dado"
        @onClose="aoFecharModal"
    >

        <template #modal-navegacao-titulo>

            Visualizar Ordem de Pagamento

        </template>

        <template #modal-conteudo>

            <ConteudoModal
                :dado="dado"
                :pagamento="pagamento"
                :aprovadoresParaExibicao="aprovadoresParaExibicao"
            ></ConteudoModal>

        </template>

    </ModalFullscreen>

</template>

<style scoped>
</style>
